
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { CurrencyProvider } from "@/hooks/use-currency";
import { LanguageProvider } from "@/hooks/use-language";
import { useVisitorTracking } from "@/hooks/use-visitor-tracking";
import { AIAssistant } from "@/components/ai-assistant/AIAssistant";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";

// Pages
import Landing from "@/pages/Landing";
import ClientLogin from "@/pages/ClientLogin";
import ClientPortal from "@/pages/ClientPortal";
import AgencyLogin from "@/pages/AgencyLogin";
import NotFound from "@/pages/NotFound";
import Dashboard from "@/pages/Dashboard";
import ServiceRequests from "@/pages/Dashboard/ServiceRequests";
import Clients from "@/pages/Dashboard/Clients";
import Projects from "@/pages/Dashboard/Projects";
import Analytics from "@/pages/Dashboard/Analytics";
import Settings from "@/pages/Dashboard/Settings";
import Subscriptions from "@/pages/Dashboard/Subscriptions";
import Expenses from "@/pages/Dashboard/Expenses";
import Invoices from "@/pages/Dashboard/Invoices";
import Reports from "@/pages/Dashboard/Reports";
import TeamMembers from "@/pages/Dashboard/TeamMembers";
import TeamChat from "@/pages/Dashboard/TeamChat";
import TeamMeetings from "@/pages/Dashboard/TeamMeetings";
import Notifications from "@/pages/Dashboard/Notifications";
import Communications from "@/pages/Dashboard/Communications";
import InvitationPage from "@/pages/InvitationPage";
import ConfirmEmail from "@/pages/ConfirmEmail";
import ClientConfirmEmail from "@/pages/ClientConfirmEmail";
import AutomationStudio from "@/pages/Dashboard/AutomationStudio";
import CRM from "@/pages/Dashboard/CRM";
import Calendar from "@/pages/Dashboard/Calendar";
import Accounting from "@/pages/Dashboard/Accounting";
import ClientHealth from "@/pages/Dashboard/ClientHealth";
import KnowledgeBase from "@/pages/Dashboard/KnowledgeBase";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import BlogManagement from "@/pages/Dashboard/BlogManagement";

const queryClient = new QueryClient();

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AgencyProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const [isAgencyMember, setIsAgencyMember] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAccess = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Check user_roles table for secure role verification
        const { data: roleData } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id)
          .maybeSingle();

        if (roleData) {
          // User has a role in user_roles, they're an agency member
          setIsAgencyMember(true);
        } else {
          // Fallback: check profile user_type for existing users
          const { data: profile } = await supabase
            .from("profiles")
            .select("user_type")
            .eq("id", user.id)
            .single();

          setIsAgencyMember(profile?.user_type === "agency_member");
        }
      } catch (error) {
        console.error("Error checking access:", error);
        setIsAgencyMember(false);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      checkAccess();
    }
  }, [user, authLoading]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/agency-login" replace />;
  }

  if (!isAgencyMember) {
    // Check if they're a client, redirect to client portal
    return <Navigate to="/client-portal" replace />;
  }

  return <>{children}</>;
};

function AppRoutes() {
  // Track all page visits for analytics
  useVisitorTracking();
  
  return (
    <Routes>
      {/* Client Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/client-login" element={<ClientLogin />} />
      <Route path="/client-portal" element={<ClientPortal />} />
      <Route path="/client-confirm-email" element={<ClientConfirmEmail />} />
      
      {/* Agency Routes */}
      <Route path="/agency-login" element={<AgencyLogin />} />
      <Route path="/confirm-email" element={<ConfirmEmail />} />
      <Route path="/invitation" element={<InvitationPage />} />
      <Route
        path="/dashboard"
        element={
          <AgencyProtectedRoute>
            <DashboardLayout />
          </AgencyProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="service-requests" element={<ServiceRequests />} />
        <Route path="clients" element={<Clients />} />
        <Route path="projects" element={<Projects />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="expenses" element={<Expenses />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="settings" element={<Settings />} />
        <Route path="subscriptions" element={<Subscriptions />} />
        <Route path="reports" element={<Reports />} />
        <Route path="team-members" element={<TeamMembers />} />
        <Route path="team-chat" element={<TeamChat />} />
        <Route path="team-meetings" element={<TeamMeetings />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="communications" element={<Communications />} />
        <Route path="automation-studio" element={<AutomationStudio />} />
        <Route path="crm" element={<CRM />} />
        <Route path="calendar" element={<Calendar />} />
        <Route path="accounting" element={<Accounting />} />
        <Route path="client-health" element={<ClientHealth />} />
        <Route path="knowledge-base" element={<KnowledgeBase />} />
        <Route path="blog-management" element={<BlogManagement />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark" storageKey="spotlight-adz-theme">
        <TooltipProvider>
          <Router>
            <AuthProvider>
              <LanguageProvider>
                <CurrencyProvider>
                  <SidebarProvider>
                    <AppRoutes />
                    <AIAssistant context="agency" />
                  </SidebarProvider>
                </CurrencyProvider>
              </LanguageProvider>
            </AuthProvider>
          </Router>
        </TooltipProvider>
        <Toaster />
        <Sonner />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
